import "./FieldCorrectionsTable.css";

/**
 * Phase 18 WU-18.F2 — Field corrections table for the document trail page.
 *
 * Rows come from the `/api/analytics/documents/:id/trail` response
 * (`fieldCorrections`). Each entry represents a single `field.update` or
 * `field.overridden` AuditEntry; the trail service maps the field id to
 * its definition name and joins the operator's display name.
 */
export interface FieldCorrection {
  fieldId: string | null;
  fieldName: string | null;
  ocrValue: string | null;
  finalValue: string | null;
  correctedBy: string;
  correctedByUserId: string;
  correctionType: "keyed" | "overridden";
  at: string; // ISO
}

export interface FieldCorrectionsTableProps {
  corrections: FieldCorrection[];
}

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "?";
}

function formatTimestamp(iso: string): string {
  // "2026-04-17T10:05:00.000Z" → "2026-04-17 10:05"
  return iso.slice(0, 16).replace("T", " ");
}

export function FieldCorrectionsTable({ corrections }: FieldCorrectionsTableProps) {
  if (corrections.length === 0) {
    return (
      <div className="field-corrections__empty" data-testid="field-corrections-empty">
        No field corrections recorded for this document.
      </div>
    );
  }

  return (
    <div className="field-corrections" data-testid="field-corrections-table">
      <table className="analytics-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>OCR Value</th>
            <th>Final Value</th>
            <th>Corrected By</th>
            <th>Type</th>
            <th>At</th>
          </tr>
        </thead>
        <tbody>
          {corrections.map((c, idx) => {
            const rowKey = c.fieldId ?? `row-${idx}`;
            const chipClass =
              c.correctionType === "overridden"
                ? "field-corrections__chip field-corrections__chip--overridden"
                : "field-corrections__chip field-corrections__chip--keyed";
            return (
              <tr
                key={rowKey}
                data-testid={`field-correction-row-${rowKey}`}
                className="field-corrections__row"
              >
                <td>{c.fieldName ?? c.fieldId ?? "—"}</td>
                <td className="field-corrections__value">{c.ocrValue ?? "—"}</td>
                <td className="field-corrections__value field-corrections__value--final">
                  {c.finalValue ?? "—"}
                </td>
                <td>
                  <span className="field-corrections__operator">
                    <span className="field-corrections__avatar" aria-hidden="true">
                      {initialOf(c.correctedBy)}
                    </span>
                    <span>{c.correctedBy}</span>
                  </span>
                </td>
                <td>
                  <span
                    className={chipClass}
                    data-testid={`field-correction-chip-${rowKey}`}
                  >
                    {c.correctionType}
                  </span>
                </td>
                <td className="field-corrections__timestamp">{formatTimestamp(c.at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
